/**
 * Authentication via Office SSO (Identity API 1.3).
 * Uses the user's current Outlook/Office session; no separate sign-in when SSO is available.
 */
import { addinLogger } from '../utils/addinLogger';

/** Whether Office.auth (SSO) is available in this host. */
export function isSsoAvailable(): boolean {
  try {
    return typeof Office !== 'undefined' && Boolean((Office as { auth?: { getAccessToken?: unknown } }).auth?.getAccessToken);
  } catch {
    return false;
  }
}

/** JWT payload shape (decode only; no verification). */
interface JwtPayload {
  aud?: string;
  iss?: string;
  appid?: string;
  oid?: string;
  upn?: string;
  preferred_username?: string;
  email?: string;
}

/**
 * Decode JWT payload (no verification; for debug logging only).
 * Returns payload or null if decode fails.
 */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** Alias for debug logging. */
function decodeJwtPayloadForDebug(token: string): { aud?: string; iss?: string; appid?: string } | null {
  return decodeJwtPayload(token);
}

/**
 * Get the current user's Azure AD object ID and UPN from a token.
 * Used to send X-User-Id / X-User-Email so the backend can check Copilot license.
 */
export function getUserIdFromToken(token: string): { oid: string; email?: string } | null {
  const payload = decodeJwtPayload(token);
  if (!payload?.oid) return null;
  const email = payload.upn ?? payload.preferred_username ?? payload.email ?? undefined;
  return { oid: payload.oid, email };
}

/** Single in-flight token request so Office SSO is not called concurrently (avoids "getAccessToken failed"). */
let inFlightPromise: Promise<string> | null = null;

/**
 * Get an access token for the current user from the Office session.
 * Use for Microsoft Graph and the add-in backend API.
 * Concurrent callers share the same request; Office SSO fails when multiple getAccessToken calls run at once.
 * @throws If Office SSO is not available or token request fails.
 */
export async function getAccessToken(): Promise<string> {
  if (!isSsoAvailable()) {
    throw new Error(
      'Sign-in is not available in this context. Office SSO is only supported in Outlook with a work or school account (not Outlook.com or Gmail). See docs/ENTRA_SSO_SETUP.md.'
    );
  }

  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = (async (): Promise<string> => {
    addinLogger.debug('AuthService: getting token via Office SSO', 'options: allowSignInPrompt=true, allowConsentPrompt=true, forMSGraphAccess=true');
    let token: string;
    try {
      token = await (Office as { auth: Office.Auth }).auth.getAccessToken({
        allowSignInPrompt: true,
        allowConsentPrompt: true,
        forMSGraphAccess: true,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      addinLogger.error('AuthService: getAccessToken failed (error from Office)', msg);
      throw err;
    } finally {
      inFlightPromise = null;
    }
    const payload = decodeJwtPayload(token);
    if (payload) {
      addinLogger.debug('AuthService: token received', `JWT aud=${payload.aud ?? 'n/a'} iss=${payload.iss ?? 'n/a'} appid=${payload.appid ?? 'n/a'}`);
      if (payload.aud && payload.aud !== 'https://graph.microsoft.com') {
        addinLogger.debug('AuthService: token audience is not Graph', 'Using this token for Graph may cause "Invalid resource"; expected for legacy SSO (use OBO or NAA for Graph).');
      }
    } else {
      addinLogger.debug('AuthService: token received', 'JWT decode failed (not a JWT or invalid base64)');
    }
    return token;
  })();

  return inFlightPromise;
}
